import { useState } from "react"
import { Box, IconButton, Stack, Tooltip } from "@mui/material"
import Iconify from "minimals-template/components/Iconify"
import { useTheme } from "@mui/material/styles"
import { BoundTextField, BoundStandardSwitch } from "lib/@components/binding/bound-components"
import { useBoundContext } from "lib/@components/binding/use-bound-context"
import { useBoundValue } from "lib/@components/binding/use-bound-value"
import { SortableItem } from "lib/@components/SortableItem"
import { DragHandle } from "lib/dragHandle"
import { RequestedFieldTypeSelect } from "./requested-field-type-select"
import { RequestedFieldEditor } from "./share-link-fields-slot-definitions"

/**
 * One editable row in the requested-information builder. Bound (via the surrounding `Repeat bind`) to a
 * single field object on `target.requestedFields`. Edits to the label / type / required / choice
 * options write straight onto that object through the `BoundXXX` components, and the share dialog
 * persists them via its normal save chain.
 *
 * Rows are reordered by drag-and-drop: the row is a `SortableItem` (the editor wraps the list in
 * `<Sortable>`) and exposes a `DragHandle`, matching the app's standard sortable-list pattern.
 *
 * @param {object} props
 * @param {number} props.index position within the requestedFields list (used for data-cy)
 * @param {Function} props.remove removes this row from the parent list (supplied by Repeat)
 */
export function RequestedFieldRow({ index = 0, remove }) {
    const theme = useTheme()
    const { onChange, target } = useBoundContext()
    const [type] = useBoundValue("type", "text")
    const [dragProps, setDragProps] = useState()

    return (
        <SortableItem id={target.id} setDragProps={setDragProps}>
            <Box
                data-cy={`requested-field-row-${index}`}
                sx={{
                    p: 1.5,
                    borderRadius: theme.shape.borderRadius / 8,
                    border: `1px solid ${theme.palette.divider}`,
                }}
            >
                <Stack direction="row" spacing={1} alignItems="flex-start">
                    <Box data-cy="requested-field-drag" sx={{ pt: 1, cursor: "grab", touchAction: "none" }}>
                        <DragHandle {...dragProps} />
                    </Box>
                    <Stack spacing={1} sx={{ flexGrow: 1, minWidth: 0 }}>
                        <Stack direction="row" spacing={1} alignItems="flex-start">
                            <Box data-cy="requested-field-label" sx={{ flexGrow: 1 }}>
                                <BoundTextField field="label" label="Label" size="small" yup="string.required" />
                            </Box>
                            <RequestedFieldTypeSelect />
                            <Box data-cy="requested-field-required">
                                <BoundStandardSwitch field="required" label="Required" sideEffects />
                            </Box>
                            <Box sx={{ display: "flex", alignItems: "center", minHeight: 40, flexShrink: 0 }}>
                                <Tooltip title="Remove field">
                                    <span>
                                        <IconButton
                                            aria-label="Remove field"
                                            size="small"
                                            color="secondary"
                                            data-cy="requested-field-remove"
                                            onClick={handleRemove}
                                        >
                                            <Iconify icon="mdi:delete" />
                                        </IconButton>
                                    </span>
                                </Tooltip>
                            </Box>
                        </Stack>
                        <RequestedFieldEditor.Slot type={type} field={target} />
                    </Stack>
                </Stack>
            </Box>
        </SortableItem>
    )

    function handleRemove() {
        remove?.()
        onChange?.()
    }
}
